import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration';
import { FileDown, Printer, Share2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface CertificatePDFGeneratorProps {
  certificate: {
    id: number;
    certificate_number: string;
    student_name?: string;
    issued_at: string;
    unique_code?: string;
    user_id: string;
    profiles?: {
      full_name?: string;
      email?: string;
      driver_license_number?: string;
      driver_license_state?: string;
      county?: string;
    };
  };
  templateId?: string;
}

export function CertificatePDFGenerator({ certificate, templateId = 'standard' }: CertificatePDFGeneratorProps) {
  const { generateDocument, logDocumentAccess, isLoading } = useDocumentGeneration();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleGenerateCertificate = async () => {
    const studentName = certificate.student_name || certificate.profiles?.full_name || 'Student';
    
    generateDocument({
      template: templateId,
      documentType: 'certificate',
      data: {
        certificateId: certificate.id,
        certificateNumber: certificate.certificate_number,
        studentName,
        issuedAt: certificate.issued_at,
        uniqueCode: certificate.unique_code,
        userId: certificate.user_id,
        driverLicense: certificate.profiles?.driver_license_number,
        driverLicenseState: certificate.profiles?.driver_license_state,
        county: certificate.profiles?.county,
        email: certificate.profiles?.email
      },
      fileName: `certificate-${certificate.certificate_number}.pdf`
    }, (result) => {
      if (result.success && result.fileUrl && result.documentId) {
        setPreviewUrl(result.fileUrl);
        setDocumentId(result.documentId);
      }
    });
  };

  const handlePreview = () => {
    if (documentId) {
      logDocumentAccess(documentId, 'view');
    }
    setPreviewOpen(true);
  };

  const handlePrint = () => {
    if (previewUrl && documentId) {
      logDocumentAccess(documentId, 'print');
      const printWindow = window.open(previewUrl, '_blank');
      printWindow?.addEventListener('load', () => {
        printWindow.print();
      });
    }
  };

  const handleDownload = () => {
    if (previewUrl && documentId) {
      logDocumentAccess(documentId, 'download');
      const a = document.createElement('a');
      a.href = previewUrl;
      a.download = `certificate-${certificate.certificate_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleShare = async () => {
    if (previewUrl && documentId) {
      logDocumentAccess(documentId, 'share');
      // In a real implementation, this would open a share dialog or email the certificate
      alert('Share functionality will be implemented in a future update.');
    }
  };

  return (
    <div>
      {!previewUrl ? (
        <Button 
          onClick={handleGenerateCertificate} 
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <FileDown className="h-4 w-4" />
          {isLoading ? 'Generating...' : 'Generate Certificate PDF'}
        </Button>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview} className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} className="flex items-center gap-2">
            <FileDown className="h-4 w-4" />
            Download
          </Button>
          <Button variant="outline" size="sm" onClick={handleShare} className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="w-full h-[70vh] overflow-auto">
              <iframe 
                src={previewUrl} 
                title="Certificate Preview" 
                className="w-full h-full border-0" 
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
